import axios from 'axios';
import config from '../config';
import authService from './authService';

// Create axios instance for Stanbic API
const stanbicApi = axios.create({
    baseURL: `${config.API_BASE_URL}${config.STANBIC_API_PREFIX}/${config.STANBIC_API_VERSION}`,
    timeout: config.TIMEOUTS.API_REQUEST,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Request interceptor: add access token
stanbicApi.interceptors.request.use(
    async (config) => {
        const token = await authService.getValidAccessToken();
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Response interceptor: handle 401
stanbicApi.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;
            try {
                const newToken = await authService.getValidAccessToken();
                if (newToken) {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return stanbicApi(originalRequest);
                } else {
                    authService.forceLogout();
                    return Promise.reject(new Error('Authentication failed'));
                }
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                authService.forceLogout();
                return Promise.reject(refreshError);
            }
        }
        return Promise.reject(error.response?.data || error.message);
    }
);

const StanbicService = {
    getConfiguration: async () => {
        try {
            const response = await stanbicApi.get(config.STANBIC.ENDPOINTS.GET_DEFAULT_CONFIG);
            return response.data;
        } catch (error) {
            console.error('Error getting Stanbic configuration:', error.message);
            throw error.message;
        }
    },

    saveConfiguration: async (configData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };

            const response = await stanbicApi.post(config.STANBIC.ENDPOINTS.CONFIGURATIONS, payload);

            return response.data;

        } catch (error) {
            console.error('Error saving Stanbic configuration:', error.message);
            throw error.message;
        }
    },

    generateSSHKey: async (configData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };


            const response = await stanbicApi.post(
                config.STANBIC.ENDPOINTS.GENERATE_SSH_KEY,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error generating SSH key:', error.message);
            throw error.message;
        }
    },

    generatePGPKey: async (configData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };

            const response = await stanbicApi.post(
                config.STANBIC.ENDPOINTS.GENERATE_PGP_KEY,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error generating SSH key:', error.message);
            throw error.message;
        }
    },

    testConnection: async (configData) => {

        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...configData,
                accessToken
            };

            const response = await stanbicApi.post(
                config.STANBIC.ENDPOINTS.TEST_CONNECTION,
                payload
            );

            return response.data;
        } catch (error) {
            console.error('Error generating SSH key:', error.message);
            throw error.message;
        }

    },

    getAuditTrails: async (filterData) => {
        try {

            const accessToken = await authService.getValidAccessToken();

            const payload = {
                ...filterData,
                accessToken
            };

            const response = await stanbicApi.post(
                config.STANBIC.ENDPOINTS.GET_AUDIT_TRAILS,
                payload
            );

            return response.data;

        } catch (error) {
            console.error('Error getting Stanbic logs:', error.message);
            throw error.message;
        }
    },

    getStanbicBatchPayments: async (filters = {}) => {
        try {

            const txnFilters = {
                dateFrom: filters.dateFrom || '',
                dateTo: filters.dateTo || '',
                searchKey: filters.searchTerm || ''
            };

            const response = await stanbicApi.post(
                config.STANBIC.ENDPOINTS.GET_BATCH_PAYMENTS,
                txnFilters
            );

            return response.data;

        } catch (error) {
            console.error('Error getting Stanbic batches:', error.message);
            throw error.message;
        }
    },

    getStanbicBatchBreakdown: async (filters = {}) => {
        try {
            const response = await stanbicApi.get(config.STANBIC.ENDPOINTS.GET_TRANSACTIONS, {
                params: filters
            });
            return response.data;
        } catch (error) {
            console.error('Error getting Stanbic breakdown:', error.message);
            throw error.message;
        }
    },

    getStanbicPaymentResponses: async (batchId) => {
        try {
            const response = await stanbicApi.get(`/payment-responses/${batchId}`);
            return response.data;
        } catch (error) {
            console.error('Error getting Stanbic payment responses:', error.message);
            throw error.message;
        }
    },

    generatePgpKey: async (keyData) => {
        try {
            const response = await stanbicApi.post('/generate-pgp-key', keyData);
            return response.data;
        } catch (error) {
            console.error('Error generating PGP key:', error);
            throw error;
        }
    }
};

export default StanbicService;